import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api';
import { markContentDeleted, restoreContent, ingestCPL } from './content.slice';
import { sortByNewest } from '../utils/sortByNewest';

export const fetchOrganizations = createAsyncThunk('organizations/fetchOrganizations', async () => {
    const response = await api.get('/organizations');
    return response.data;
});

export const createOrganization = createAsyncThunk('organization/create', async ({ organizationBody }) => {
    const response = await api.post('/organizations', organizationBody);
    return response.data;
});

export const fetchOrganization = createAsyncThunk('organizations/fetchOrganization', async ({ orgId }) => {
    const response = await api.get(`/organizations/${orgId}`);
    return response.data;
});

export const updateOrganization = createAsyncThunk('users/update', async ({ organizationBody, orgId }) => {
    const response = await api.put(`/organizations/${orgId}`, organizationBody);
    return response.data;
});

export const deleteOrganization = createAsyncThunk('users/delete', async (organizationId) => {
    const response = await api.delete(`/organizations/${organizationId}`)
    return response.data;
});

export const addAdminNote = createAsyncThunk('organizations/addAdminNote', async ({ orgId, note }) => {
    const response = await api.post(`/organizations/${orgId}/admin-notes`, note);
    return response.data;
});

export const updateAdminNote = createAsyncThunk('organizations/updateAdminNote', async ({ orgId, noteId, note }) => {
    const response = await api.put(`/organizations/${orgId}/admin-notes/${noteId}`, note);
    return response.data;
});

export const deleteAdminNote = createAsyncThunk('organizations/deleteAdminNote', async ({ orgId, noteId }) => {
    const response = await api.delete(`/organizations/${orgId}/admin-notes/${noteId}`);
    return response.data;
});

export const fetchOrganizationUsers = createAsyncThunk('organizations/fetchOrganizationUsers', async ({ token, orgId }) => {
    const response = await api.get(`/organizations/${orgId}/users`);
    return response.data;
});

export const generateDefaultUsers = createAsyncThunk('organizations/generateDefaultUsers', async ({ orgId, restrictions }, { rejectWithValue }) => {
    try {
        const response = await api.post(`/organizations/${orgId}/default-users`, { restrictions });
        return response.data; // { results, users }
    } catch (err) {
        return rejectWithValue(err.response?.data?.message || 'Error generating default users');
    }
});

export const fetchOrganizationContent = createAsyncThunk('organizations/fetchOrganizationContent', async ({ token, orgId }) => {
    const response = await api.get(`/organizations/${orgId}/content`);
    return response.data;
});

export const togglePinnedContent = createAsyncThunk('organizations/togglePinnedContent', async (contentId) => {
    const response = await api.post(`/content/${contentId}/toggle-pinned`);
    return response.data;
});

// Shared handler for actions that return a single updated organization.
const setOrganization = (state, action) => {
    state.organizationStatus = 'success';
    state.organization = action.payload;
    state.organizationError = null;
    const index = state.list.findIndex(org => org._id === action.payload._id);
    if (index !== -1) {
        state.list[index] = action.payload;
    }
};

const organizationsSlice = createSlice({
    name: 'organizations',
    initialState: {
        list: [],
        status: 'idle', // 'idle' | 'loading' | 'success' | 'failed'
        error: null,
        organization: null,
        organizationStatus: 'idle', // 'idle' | 'loading' | 'success' | 'failed'
        organizationError: null,
        organizationUsers: [],
        organizationUsersStatus: 'idle', // 'idle' | 'loading' | 'success' | 'failed'
        organizationUsersError: null,
        organizationContent: [],
        organizationContentStatus: 'idle', // 'idle' | 'loading' | 'success' | 'failed'
        organizationContentError: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchOrganizations.pending, (state) => {
                state.status = 'loading';
                state.list = [];
            })
            .addCase(fetchOrganizations.fulfilled, (state, action) => {
                state.status = 'success';
                state.list = sortByNewest(action.payload);
                state.error = null;
            })
            .addCase(fetchOrganizations.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'An error occurred';
            })

            .addCase(fetchOrganization.pending, (state) => {
                state.organizationStatus = 'loading';
            })
            .addCase(fetchOrganization.fulfilled, (state, action) => {
                state.organizationStatus = 'success';
                state.organization = action.payload;
                state.organizationError = null;
            })
            .addCase(fetchOrganization.rejected, (state, action) => {
                state.organizationStatus = 'failed';
                state.organizationError = action.error.message || 'An error occurred';
            })

            .addCase(updateOrganization.pending, (state) => {
                state.organizationStatus = 'loading';
            })
            .addCase(updateOrganization.fulfilled, (state, action) => {
                state.organizationStatus = 'success';
                state.organization = action.payload;
                state.organizationError = null;
                // Update the organization in the list as well
                const index = state.list.findIndex(org => org._id === action.payload._id);
                if (index !== -1) {
                    state.list[index] = action.payload;
                }
            })
            .addCase(updateOrganization.rejected, (state, action) => {
                state.organizationStatus = 'failed';
                state.organizationError = action.error.message || 'An error occurred';
            })

            .addCase(addAdminNote.fulfilled, setOrganization)
            .addCase(updateAdminNote.fulfilled, setOrganization)
            .addCase(deleteAdminNote.fulfilled, setOrganization)

            .addCase(fetchOrganizationUsers.pending, (state) => {
                state.organizationUsersStatus = 'loading';
            })
            .addCase(fetchOrganizationUsers.fulfilled, (state, action) => {
                state.organizationUsersStatus = 'success';
                state.organizationUsers = action.payload;
                state.organizationUsersError = null;
            })
            .addCase(fetchOrganizationUsers.rejected, (state, action) => {
                state.organizationUsersStatus = 'failed';
                state.organizationUsersError = action.error.message || 'An error occurred';
            })

            // The endpoint returns the refreshed user list; swap it straight in
            // (no pending → 'loading' so the list doesn't flicker mid-request).
            .addCase(generateDefaultUsers.fulfilled, (state, action) => {
                state.organizationUsersStatus = 'success';
                state.organizationUsers = action.payload.users;
                state.organizationUsersError = null;
            })
            .addCase(generateDefaultUsers.rejected, (state, action) => {
                state.organizationUsersError = action.payload || action.error.message || 'An error occurred';
            })

            .addCase(fetchOrganizationContent.pending, (state) => {
                state.organizationContentStatus = 'loading';
                state.organizationContent = [];
            })
            .addCase(fetchOrganizationContent.fulfilled, (state, action) => {
                state.organizationContentStatus = 'success';
                state.organizationContent = sortByNewest(action.payload);
                state.organizationContentError = null;
            })
            .addCase(fetchOrganizationContent.rejected, (state, action) => {
                state.organizationContentStatus = 'failed';
                state.organizationContentError = action.error.message || 'An error occurred';
            })

            .addCase(togglePinnedContent.pending, (state) => {
                state.organizationContentStatus = 'loading';
            })
            .addCase(togglePinnedContent.fulfilled, (state, action) => {
                state.organizationContentStatus = 'success';
                state.organizationContentError = null;
                const updatedContent = action.payload;
                const index = state.organizationContent.findIndex(
                    (content) => content._id === updatedContent._id
                );
                if (index !== -1) {
                    state.organizationContent[index] = updatedContent;
                }
            })
            .addCase(togglePinnedContent.rejected, (state, action) => {
                state.organizationContentStatus = 'failed';
                state.organizationContentError = action.error.message || 'An error occurred';
            })

            .addCase(markContentDeleted.fulfilled, (state, action) => {
                const updatedContent = action.payload;
                const index = state.organizationContent.findIndex(c => c._id === updatedContent._id);
                if (index !== -1) {
                    state.organizationContent[index] = updatedContent;
                }
            })

            .addCase(restoreContent.fulfilled, (state, action) => {
                const updatedContent = action.payload;
                const index = state.organizationContent.findIndex(c => c._id === updatedContent._id);
                if (index !== -1) {
                    state.organizationContent[index] = updatedContent;
                }
            })

            // Reflect the "transferring to mediablock" marker on the ingested CPL in place so the
            // card shows the transferring badge without a refetch.
            .addCase(ingestCPL.fulfilled, (state, action) => {
                const updatedContent = action.payload;
                if (!updatedContent?._id) return;
                const index = state.organizationContent.findIndex(c => c._id === updatedContent._id);
                if (index !== -1) {
                    state.organizationContent[index] = updatedContent;
                }
            })
        ;
    },
});

export default organizationsSlice.reducer;
